import { Component, Input, OnChanges, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CustomCurrencyPipe } from '@shared/pipe/custom-currency/custom-currency.pipe';
import { monthNames } from '@shared/globals/month-names';
import { BaseChartComponent } from '@shared/component/charts/base-chart/base-chart.component';
import { chart } from 'highcharts';
import { COLORS } from 'taxtank-core';

@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BarChartComponent extends BaseChartComponent implements OnInit, OnChanges {
  // all bars in one (stacked columns)
  @Input() stacked: boolean;
  @Input() compare: boolean;
  @Input() yAxisLabelEnabled = false;

  ngOnInit(): void {
    this.initChart();
  }

  ngOnChanges(): void {
    this.initChart();
  }

  initChart(): void {
    if (!this.data.length) {
      return;
    }

    this.buildData();
    // set options declared in base class
    this.setDefaultOptions(this.customCurrencyPipe);
    // set options
    this.setOptions(this.customCurrencyPipe);
    // hack for dynamic containers
    setTimeout(() => {
      this.showChart();
    });
  }

  buildData(): void {
    this.chartData = this.data.map((item: any) => ({
      name: item[this.bindSeriesName],
      series: item[this.bindSeriesData].map((serie: any) => {
        if (this.isDateChart) {
          // if (!isNaN(new Date(serie[this.bindX]).getTime())) {
          const date: Date = new Date(serie[this.bindX]);
          return {
            name: serie[this.bindX],
            tooltip: serie.tooltip ?? serie,
            x: Date.UTC(date.getFullYear(), date.getMonth()),
            y: serie[this.bindY]
          };
          // }
        }

        return {
          name: serie[this.bindX],
          tooltip: serie.tooltip ?? serie,
          y: serie[this.bindY]
        };
      }),
      stack: item.stack,
    }));
  }

  setOptions(customCurrencyPipe: CustomCurrencyPipe): void {
    // show/hide Y axis
    this.chartOptions.yAxis.plotLines = [{
      value: 0,
      width: 1,
      color: '#aaa',
      zIndex: 10
    }];
    this.chartOptions.yAxis.gridLineWidth = 0;
    this.chartOptions.yAxis.minorGridLineWidth = 0;
    this.chartOptions.yAxis.labels.enabled = this.yAxisLabelEnabled;

    // set x axis labels type
    this.chartOptions.xAxis.type = !this.isDateChart ? 'category' : 'datetime';

    // set chart type
    this.chartOptions.chart.type = 'column';

    // set columns stacking
    this.chartOptions.plotOptions.series.stacking = this.stacked ? 'normal' : '';
    // set top border radius
    this.chartOptions.plotOptions.series.borderRadius = 6;
    // set max bar width
    this.chartOptions.plotOptions.series.maxPointWidth = 40;

    const self = this;

    // set tooltip template
    this.setTooltipFormatter((context): string => {
      const date: Date = new Date(context.point.x);
      const label: string = isNaN(context.point.x) ? `${monthNames[date.getMonth()]} ${date.getFullYear()}` : context.point.name;

      return `<span style="color: ${COLORS.grey}; font-size: 12px; line-height: 18px; font-weight: bold; text-transform: uppercase; font-family: Montserrat, sans-serif">
                    ${label}
                  </span>
                  <br>
                  <span style="color:${context.point.color}; font-size: 12px;">&#9679;</span>
                  <span style="font-size: 12px;">
                    ${context.point.series.name}: ${customCurrencyPipe.transform(context.point.y, { decimals: 0, currency: self.currency })}
                  </span>`;
    });

    // set series to chart
    this.chartOptions.series = this.chartData.map((d: any) => ({
      name: d.name,
      data: d.series,
      stack: d.stack,
    }));
    // Bar chart options redefine yAxis.plotLines after base options, so we need to reapply hidden x-axis settings.
    this.hideXAxisLine();
  }

  showChart(): void {
    chart(this.containerId, this.chartOptions);
  }
}
